import { CalendarDays, RefreshCw } from 'lucide-react';
import { useI18n } from '@/hooks/useI18n';
import { Button } from '@/components/ui/button';
import { TextReveal } from '@/components/primitives/TextReveal';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { PERIOD_DAYS_OPTIONS } from '@/lib/constants';
import { formatRelative } from '@/lib/format';
import { cn } from '@/lib/cn';

interface AnalyticsHeaderProps {
  periodDays: number;
  onPeriodChange: (days: number) => void;
  updatedAt: number;
  refreshing: boolean;
  onRefresh: () => void;
}

export function AnalyticsHeader({
  periodDays,
  onPeriodChange,
  updatedAt,
  refreshing,
  onRefresh,
}: AnalyticsHeaderProps) {
  const { t } = useI18n();
  const freshness = refreshing
    ? t('Actualisation…', 'Refreshing…')
    : updatedAt
      ? t(
          `Page actualisée ${formatRelative(new Date(updatedAt).toISOString())}`,
          `Page refreshed ${formatRelative(new Date(updatedAt).toISOString())}`,
        )
      : t('Chargement des données…', 'Loading data…');
  return (
    <header className="grid min-w-0 grid-cols-[minmax(0,1fr)_128px_44px] items-center gap-x-2 gap-y-3 sm:grid-cols-[minmax(0,1fr)_170px_44px] sm:gap-x-3">
      <TextReveal className="min-w-0">
        <p className="t-stagger-line mb-1.5 text-[9px] font-semibold uppercase tracking-[0.16em] text-violet-deep">
          {t('Performance', 'Performance')}
        </p>
        <h1 className="t-stagger-line font-display text-[24px] leading-tight text-text-1 sm:text-[30px]">
          Analytics
        </h1>
      </TextReveal>
      <Select value={String(periodDays)} onValueChange={(value) => onPeriodChange(Number(value))}>
        <SelectTrigger
          aria-label={t('Période', 'Period')}
          className="h-11 w-full min-w-0 gap-1.5 px-2.5 text-[12px] sm:h-11"
        >
          <CalendarDays
            className="hidden size-3.5 shrink-0 text-text-3 sm:block"
            aria-hidden="true"
          />
          <SelectValue>
            {periodDays === 0
              ? t('Tout l’historique', 'All time')
              : t(`${periodDays} jours`, `${periodDays} days`)}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {PERIOD_DAYS_OPTIONS.map((option) => (
            <SelectItem key={option.value} value={String(option.value)}>
              {option.value === 0
                ? t('Tout l’historique', 'All time')
                : t(option.label, option.labelEn)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Button
        variant="secondary"
        size="icon"
        onClick={onRefresh}
        disabled={refreshing}
        aria-label={t('Actualiser', 'Refresh')}
        title={freshness}
        className="size-11 sm:size-11"
      >
        <RefreshCw
          className={cn('size-4', refreshing && 'motion-safe:animate-spin')}
          aria-hidden="true"
        />
      </Button>
      <p className="col-span-3 text-[12px] leading-relaxed text-text-3 sm:text-[13px]">
        {t(
          'Suivez vos résultats et repérez les comptes qui font la différence.',
          'Track your results and see which accounts make the difference.',
        )}
      </p>
      <p className="sr-only" aria-live="polite">
        {freshness}
      </p>
    </header>
  );
}
